import React, { useEffect, useState } from "react";
import { Button, Card, Col, Empty, Popconfirm, Row, Space, Spin, Typography, Upload, message } from "antd";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { deleteAdminPageImage, getPageImages, uploadAdminPageImage } from "../services/api";
import { DefaultSketchLoading, PageLoadingOverlay } from "./RoomLoadingScene";

const { Text, Title } = Typography;
const allowedImageTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const allowedVideoTypes = ["video/mp4", "video/webm"];

const readImageMetadata = file => new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
        const dimensions = { width: image.naturalWidth, height: image.naturalHeight, duration: 0 };
        URL.revokeObjectURL(objectUrl);
        resolve(dimensions);
    };
    image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("图片读取失败"));
    };
    image.src = objectUrl;
});

const readVideoMetadata = file => new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.onloadedmetadata = () => {
        const metadata = { width: video.videoWidth, height: video.videoHeight, duration: video.duration };
        URL.revokeObjectURL(objectUrl);
        resolve(metadata);
    };
    video.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("视频读取失败"));
    };
    video.src = objectUrl;
});

const PageImageManagement = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingSlot, setSavingSlot] = useState("");

    const load = async () => {
        try {
            const response = await getPageImages();
            setItems(Array.isArray(response?.data) ? response.data : []);
        } catch (error) {
            message.error(error?.error || "页面图片加载失败");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const beforeUpload = async (file, item) => {
        const isVideo = allowedVideoTypes.includes(file.type);
        const allowed = allowedImageTypes.includes(file.type) || (item.allow_video && isVideo);
        if (!allowed) {
            message.error(item.allow_video ? "仅支持JPG、PNG、WEBP、GIF、MP4或WEBM" : "仅支持JPG、PNG、WEBP或GIF图片");
            return Upload.LIST_IGNORE;
        }
        if (file.size > 10 * 1024 * 1024) {
            message.error("页面素材不能超过10MB");
            return Upload.LIST_IGNORE;
        }
        try {
            const metadata = isVideo ? await readVideoMetadata(file) : await readImageMetadata(file);
            if (isVideo && item.max_duration_seconds && metadata.duration > item.max_duration_seconds + 0.05) {
                message.error(`视频最长${item.max_duration_seconds}秒，当前约${metadata.duration.toFixed(1)}秒`);
                return Upload.LIST_IGNORE;
            }
            if (metadata.width !== item.width || metadata.height !== item.height) {
                message.warning(`当前素材为${metadata.width}×${metadata.height}px，推荐${item.width}×${item.height}px；仍可上传，系统会自动裁切适配。`, 6);
            }
        } catch (error) {
            message.error("无法读取页面素材，请更换后重试");
            return Upload.LIST_IGNORE;
        }
        return true;
    };

    const upload = async (item, file, onSuccess, onError) => {
        try {
            setSavingSlot(item.slot);
            const formData = new FormData();
            formData.append("file", file);
            const response = await uploadAdminPageImage(item.slot, formData);
            setItems(Array.isArray(response?.data) ? response.data : []);
            message.success("页面素材已更新，手机和电脑端刷新后生效");
            onSuccess?.(response);
        } catch (error) {
            message.error(error?.error || "上传失败");
            onError?.(error);
        } finally {
            setSavingSlot("");
        }
    };

    const remove = async item => {
        try {
            setSavingSlot(item.slot);
            const response = await deleteAdminPageImage(item.slot);
            setItems(Array.isArray(response?.data) ? response.data : []);
            message.success(item.default_text || "已恢复默认页面样式");
        } catch (error) {
            message.error(error?.error || "移除失败");
        } finally {
            setSavingSlot("");
        }
    };

    return (
        <div>
            {loading && <PageLoadingOverlay caption="正在加载页面素材，请稍候…" />}
            <Title level={4} style={{ marginTop: 0 }}>页面图片管理</Title>
            <Text type="secondary">电脑端和手机端可分别上传合适比例的素材；图片支持JPG、PNG、WEBP、GIF，等待画面另支持MP4、WEBM短视频，单个不超过10MB。</Text>
            <Spin spinning={loading}>
                {!loading && !items.length ? <Empty description="暂无可管理的图片位置" /> : <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    {items.map(item => <Col key={item.slot} xs={24} md={12} xl={8}>
                        <Card className="page-image-card" title={item.label}>
                            <Text strong>推荐尺寸：{item.width} × {item.height} px</Text>
                            <div><Text type="secondary">系统会按页面区域自动裁切，按推荐比例效果最佳。{item.max_duration_seconds ? ` 视频最长${item.max_duration_seconds}秒。` : ""}</Text></div>
                            <div className="page-image-preview">
                                {item.image_url
                                    ? (item.media_type === "video"
                                        ? <video src={item.image_url} aria-label={item.label} muted autoPlay loop playsInline controls />
                                        : <img src={item.image_url} alt={item.label} />)
                                    : (item.default_text ? <DefaultSketchLoading /> : <span>暂未上传，页面保持默认样式</span>)}
                            </div>
                            <Space wrap>
                                <Upload
                                    accept={item.allow_video ? "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm" : "image/jpeg,image/png,image/webp,image/gif"}
                                    showUploadList={false}
                                    beforeUpload={file => beforeUpload(file, item)}
                                    customRequest={({ file, onSuccess, onError }) => upload(item, file, onSuccess, onError)}
                                >
                                    <Button type="primary" icon={<UploadOutlined />} loading={savingSlot === item.slot}>{item.image_url ? "更换素材" : "上传素材"}</Button>
                                </Upload>
                                <Popconfirm
                                    title="删除这张页面图片？"
                                    description={item.default_text || "删除后将恢复默认空白样式。"}
                                    okText="确认删除"
                                    cancelText="取消"
                                    disabled={!item.image_url}
                                    onConfirm={() => remove(item)}
                                >
                                    <Button danger disabled={!item.image_url} icon={<DeleteOutlined />} loading={savingSlot === item.slot}>删除图片</Button>
                                </Popconfirm>
                            </Space>
                        </Card>
                    </Col>)}
                </Row>}
            </Spin>
        </div>
    );
};

export default PageImageManagement;
